import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PageHeader} from "../components/layout/PageHeader";
import {Button, Radio, Col, Form, Input, Row, Select, Spin, Space, Switch, Upload} from "antd";
import {
    getCompanyGuid,
    getCompanyMe,
    updateCompanyGuid,
    updateCompanyStatus,
    useCompanyGuid,
    useCompanyStatusUpdate
} from "../hooks/company";
import {downloadWatermarkPreviewDocument} from "../hooks/certificate";
import {useHistory} from "react-router-dom";
import {CompanyCreateEditDto} from "../generated-api";
import {getClassifierCountries, useCountriesClassifier} from "../hooks/classifier";
import {BackButton} from "../components/layout/BackButton";
import {isCompanyAdmin, isTraCertAdmin, useUserMe} from "../hooks/user";
import {COMPANY_STATUS_ACTIVE} from "../constants/company";
import {LoadingOutlined, PlusOutlined} from '@ant-design/icons';
import fileDownload from "js-file-download";
import type {RcFile} from 'antd/es/upload/interface';
import {showError} from "../components/Notification";


const { TextArea } = Input;

export function CompanyPage({match}: any) {
    const {companyId} = (match || {}).params || {};
    const {t} = useTranslation();
    const history = useHistory();
   
    const currentUser = useUserMe().data || {};
    const companyResponse = useCompanyGuid();
    const countriesResponse = useCountriesClassifier();
    const countries = countriesResponse.data || [];
    const isUserCompanyAdmin = isCompanyAdmin();
    const isUserTraCertAdmin = isTraCertAdmin();
    const company = {...companyResponse.data};
    const businessSector = (company || {}).businessSector || {};
    const companyStatusUpdateResponse = useCompanyStatusUpdate();
    const [watermarkEnabled, setWatermarkEnabled] = useState(false);
    const [watermarkLoading, setWatermarkLoading] = useState(false);
    const [watermarkImg, setWatermarkImg] = useState('');
    const [watermarkText, setWatermarText] = useState('');

    useEffect(() => { setWatermarkEnabled(company.watermark) }, [company.watermark]);
    useEffect(() => { setWatermarText(company.watermarkData?.watermarkText) }, [company.watermarkData?.watermarkText]);
    useEffect(() => { setWatermarkImg(company.watermarkData?.watermarkImgBase64) },[company.watermarkData?.watermarkImgBase64]);

    useEffect(() => {
        if (companyId) {
            getCompanyGuid(companyId, true).then().catch(error => {
                console.error(error);
                history.push('/companies');
            });
            if (!countries.length) {
                getClassifierCountries().then().catch(console.error);
            }
        } else if (isUserCompanyAdmin) {
            getCompanyMe().then().catch(console.error);
        } else {
            history.push('/companies');
        }
    }, [companyId]);

    const updateStatusDisabled = currentUser.companyId === company.id;

    const onFinish = (values: CompanyCreateEditDto): void => {
        
        if (watermarkImg && values.watermarkData !== undefined){
            values.watermarkData.watermarkImgBase64 = watermarkImg;
        }

        if (watermarkText && values.watermarkData !== undefined){
            values.watermarkData.watermarkText = watermarkText;
        }
        
        updateCompanyGuid(company.id, values)
            .then(() => getCompanyGuid(company.id, true))
            .then(() => history.push('/companies'))
            .catch(console.error);
    };

    const onStatusUpdate = (values: any): void => {
        const {status} = values;
        if (status) {
            updateCompanyStatus(company.id, status).then().catch(console.error);
        }
    };

    const previewWatermark = () => {
        downloadWatermarkPreviewDocument(watermarkImg, watermarkText)
        .then((response) => response.blob())
        .then((response) => fileDownload(response, "watermark-preview.pdf" as string))
        .catch(console.error)
    }

    const getBase64 = (img: any, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result?.toString() || ''));
        reader.readAsDataURL(img);
    };
    
    const uploadButton = (
        <div>
          {watermarkLoading ? <LoadingOutlined /> : <PlusOutlined />}
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      );

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        }
    };
    
    return (
        <Spin spinning={companyResponse.loading || countriesResponse.loading || companyStatusUpdateResponse.loading}>
            <PageHeader/>
            <div className={'top-block accent3-background'}>
                <h1 className={'h1'}><BackButton backPath={'/companies'}/>
                    {t(isUserCompanyAdmin ? 'company.readOnlyTitle' : 'company.title')}
                </h1>
            </div>
            <div className={'subpage-content'}>
                <Form name="companyStatus" key={'companyStatus_' + companyResponse.loading}
                      onFinish={onStatusUpdate}
                      autoComplete={'off'}
                      labelCol={{span: 11}}
                      initialValues={{status: company.status === COMPANY_STATUS_ACTIVE ? 'true' : 'false'}}
                      validateTrigger={'onSubmit'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.status.title')} name={'status'}>
                                <Select style={{width: '100%'}} disabled={updateStatusDisabled}>
                                    <Select.Option key={'true'} value={'true'}>
                                        {t('company.status.active')}
                                    </Select.Option>
                                    <Select.Option key={'false'} value={'false'}>
                                        {t('company.status.disabled')}
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item className={'text-right'}>
                        <Button type="primary" className={'accent3-background text-white w-100'} htmlType="submit"
                                disabled={updateStatusDisabled}>
                            {t('company.updateStatus')}
                        </Button>
                    </Form.Item>
                </Form>
                <Form name="company" key={'company_' + companyResponse.loading}
                      onFinish={onFinish}
                      autoComplete={'off'}
                      labelCol={{span: 11}}
                      validateMessages={validateMessages}
                      initialValues={company}
                      //onValuesChange={(prev, next) => { setFormChanged(prev !== next)} }
                      validateTrigger={'onSubmit'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.name')} name={'name'} rules={[{required: true}, {max: 512}]}>
                                <Input readOnly={isUserCompanyAdmin}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.businessSector')}>
                                <Input readOnly value={businessSector.name}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.registryCode')} name={'registryCode'}
                                       rules={[{required: true}, {max: 50}]}>
                                <Input readOnly={isUserCompanyAdmin}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.vatNumber')} name={'vatNumber'} rules={[{max: 256}]}>
                                <Input readOnly={isUserCompanyAdmin}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.generalPhone')} name={'generalPhone'} rules={[{max: 50}]}>
                                <Input readOnly={!(isUserCompanyAdmin || isUserTraCertAdmin)}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.generalEmail')} name={'generalEmail'} rules={[{max: 256}]}>
                                <Input readOnly={!(isUserCompanyAdmin || isUserTraCertAdmin)}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.country')} name={'country'}>
                                <Select allowClear showSearch style={{width: '100%'}}
                                        placeholder={t('general.notSelected')}
                                        disabled={isUserCompanyAdmin}
                                        filterOption={(input: any, option: any): any =>
                                            String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                        }>
                                    {countries.map((country: any) =>
                                        <Select.Option key={country.id}
                                                       value={country.name}>{country.name}</Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.city')} name={'city'} rules={[{max: 100}]}>
                                <Input readOnly={isUserCompanyAdmin}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.street')} name={'street'} rules={[{max: 50}]}>
                                <Input readOnly={isUserCompanyAdmin}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.postalCode')} name={'postalCode'} rules={[]}>
                                <Input readOnly={isUserCompanyAdmin}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.disclaimer')} name={'disclaimer'} rules={[{max: 1024}]}>
                                <TextArea readOnly={!(isUserCompanyAdmin || isUserTraCertAdmin)}  rows={5} style={{height: "auto"}}/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item valuePropName="checked" label={t('company.watermark')} name={'watermark'}>
                                <Switch disabled={!isUserTraCertAdmin} checked={watermarkEnabled} onChange={setWatermarkEnabled}/>
                            </Form.Item>
                            {
                                watermarkEnabled 
                                ? <>
                                    <Form.Item label={t('company.watermark-img')} name={['watermarkData', 'watermarkImgBase64']}  rules={[{required: true}]}>
                                    <Upload 
                                        
                                        disabled={!isUserTraCertAdmin}
                                        listType="picture-card"
                                        className="avatar-uploader"
                                        showUploadList={false}
                                        beforeUpload={file => {
                                            const isImg = file.type.startsWith('image/');
                                            if (!isImg) {
                                                showError(t('company.watermark-img'), t('company.watermark-img-file-error'));
                                                return true;
                                            }

                                            const isLt1M = file.size / 1024 / 1024 < 1;
                                            if (!isLt1M) {
                                                showError(t('company.watermark-img'), t('company.watermark-img-size-error'));
                                                return true;
                                            }

                                            setWatermarkLoading(true);
                                            getBase64(file as RcFile, url => {
                                                setWatermarkLoading(false);
                                                setWatermarkImg(url);
                                            });
                                            return false;
                                        }}
                                        >
                                            {watermarkImg ? <img src={watermarkImg} alt="avatar" style={{ width: '100%' }} /> : uploadButton}
                                    </Upload>
                                    </Form.Item>
                                    <Form.Item label={t('company.watermark-text')} name={['watermarkData', 'watermarkText']} rules={[{max: 120}]}>
                                        <TextArea readOnly={!isUserTraCertAdmin} value={watermarkText} onChange={(e) => setWatermarText(e.target.value)}  rows={3} style={{height: "auto"}} maxLength={120}/>
                                    </Form.Item>
                                    <Form.Item label={t('Preview watermark')} >
                                        <Button className={'accent3-background text-white'} onClick={() => previewWatermark()} disabled={watermarkImg ? false : true}>Preview</Button>
                                    </Form.Item>
                                </>
                                : <></>
                            }
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('company.downloadType.title')} name={'downloadType'}> 
                                <Radio.Group className="pt-05" disabled={company.downloadType === 0}>
                                    <Space direction="vertical">
                                        <Radio value={1}>{t('company.downloadType.optionMeta')}</Radio>
                                        <Radio value={2}>{t('company.downloadType.optionMetaAndFiles')}</Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    {(isUserCompanyAdmin || isUserTraCertAdmin) && <Form.Item className={'no-margin text-right'}>
                        <Button type="primary" className={'accent3-background text-white w-100'} htmlType="submit">
                            {t('company.save')}
                        </Button>
                    </Form.Item>
                     
                    }
                </Form>
            </div>
        </Spin>
    )
}
